(function () {
    const SELECTORS = {
        card: '.product-item',
        sizeButton: '[data-card-size]',
        variantInput: '[data-variant-input]',
        submitButton: '[data-card-button]',
        mediaLink: '[data-card-media]'
    };

    const CARD_LINK_DATA_KEYS = {
        initialized: 'cardLinkReady',
        url: 'productLinkUrl',
        title: 'productLinkTitle'
    };

    const INTERACTIVE_ELEMENTS_SELECTOR = [
        'a[href]',
        'button',
        'input',
        'select',
        'textarea',
        'label',
        'form',
        '[role="button"]',
        '[data-card-button]',
        '[data-card-size]',
        '[data-product-link-exempt]'
    ].join(', ');

    const LABELS = {
        addToCart: (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart',
        soldOut: (window.variantStrings && window.variantStrings.soldOut) || 'Sold out'
    };

    const CARD_SELECTOR = SELECTORS.card;

    function sanitizeUrl(value) {
        if (!value) return '';
        const trimmed = value.toString().trim();
        if (!trimmed || trimmed === '#' || /^javascript:/i.test(trimmed)) {
            return '';
        }
        return trimmed;
    }

    function extractTextContent(element) {
        if (!element) return '';
        const text = element.getAttribute('data-product-title') || element.textContent || '';
        return text.trim();
    }

    function resolveCardLinkData(card) {
        if (!card) return null;

        const directUrl = sanitizeUrl(card.getAttribute('data-product-url') || card.dataset.productUrl);
        const directTitle = (card.getAttribute('data-product-title') || card.dataset.productTitle || '').trim();

        if (directUrl) {
            let resolvedTitle = directTitle;
            if (!resolvedTitle) {
                const titleSource = card.querySelector('[data-product-title]') || card.querySelector('.card-title');
                resolvedTitle = extractTextContent(titleSource);
            }
            return {
                url: directUrl,
                title: resolvedTitle
            };
        }

        const titleLink = card.querySelector('[data-product-url]');
        if (titleLink) {
            const url = sanitizeUrl(titleLink.getAttribute('data-product-url') || titleLink.getAttribute('href'));
            if (url) {
                return {
                    url,
                    title: extractTextContent(titleLink)
                };
            }
        }

        const fallbackLink = card.querySelector('.card-title a[href], a.card-title[href], a[href*="/products/"]');
        if (fallbackLink) {
            const url = sanitizeUrl(fallbackLink.getAttribute('href'));
            if (url) {
                return {
                    url,
                    title: extractTextContent(fallbackLink)
                };
            }
        }

        return null;
    }

    function ensureCardLink(card) {
        if (!card) return null;

        const existingUrl = sanitizeUrl(card.dataset[CARD_LINK_DATA_KEYS.url]);
        const existingTitle = card.dataset[CARD_LINK_DATA_KEYS.title] || '';
        if (existingUrl) {
            if (!card.dataset[CARD_LINK_DATA_KEYS.initialized]) {
                finalizeCardLink(card, existingUrl, existingTitle);
            }
            return { url: existingUrl, title: existingTitle };
        }

        const data = resolveCardLinkData(card);
        if (!data || !data.url) {
            return null;
        }

        finalizeCardLink(card, data.url, data.title);
        return data;
    }

    function finalizeCardLink(card, url, title) {
        card.dataset[CARD_LINK_DATA_KEYS.url] = url;
        if (title) {
            card.dataset[CARD_LINK_DATA_KEYS.title] = title;
        }

        if (!card.classList.contains('is-clickable')) {
            card.classList.add('is-clickable');
        }

        if (!card.hasAttribute('tabindex')) {
            card.setAttribute('tabindex', '0');
        }

        if (!card.hasAttribute('role')) {
            card.setAttribute('role', 'link');
        }

        if (!card.hasAttribute('aria-label') && title) {
            card.setAttribute('aria-label', title);
        }

        card.dataset[CARD_LINK_DATA_KEYS.initialized] = 'true';
    }

    function getSelectionText() {
        if (typeof window.getSelection !== 'function') return '';
        const selection = window.getSelection();
        return selection ? selection.toString() : '';
    }

    function targetWithinInteractive(event) {
        if (!event || !event.target) return false;
        return Boolean(event.target.closest(INTERACTIVE_ELEMENTS_SELECTOR));
    }

    function openProductLink(url, { newTab = false } = {}) {
        if (!url) return;
        if (newTab) {
            window.open(url, '_blank', 'noopener');
        } else {
            window.location.assign(url);
        }
    }

    function handleDelegatedClick(event) {
        if (!event || event.defaultPrevented || (typeof event.button === 'number' && event.button !== 0)) {
            return;
        }

        const card = event.target.closest(CARD_SELECTOR);
        if (!card) return;

        if (targetWithinInteractive(event)) {
            return;
        }

        const linkData = ensureCardLink(card);
        if (!linkData || !linkData.url) {
            return;
        }

        if (getSelectionText()) {
            return;
        }

        const shouldOpenNewTab = Boolean(event.metaKey || event.ctrlKey || event.shiftKey);
        if (shouldOpenNewTab) {
            event.preventDefault();
            openProductLink(linkData.url, { newTab: true });
            return;
        }

        openProductLink(linkData.url);
    }

    function handleDelegatedAuxClick(event) {
        if (!event || event.defaultPrevented || typeof event.button !== 'number' || event.button !== 1) {
            return;
        }

        const card = event.target.closest(CARD_SELECTOR);
        if (!card) return;

        if (targetWithinInteractive(event)) {
            return;
        }

        const linkData = ensureCardLink(card);
        if (!linkData || !linkData.url) {
            return;
        }

        event.preventDefault();
        openProductLink(linkData.url, { newTab: true });
    }

    function handleDelegatedKeydown(event) {
        if (!event || event.defaultPrevented) return;

        const card = event.target.closest(CARD_SELECTOR);
        if (!card || event.target !== card) {
            return;
        }

        const linkData = ensureCardLink(card);
        if (!linkData || !linkData.url) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const shouldOpenNewTab = Boolean(event.metaKey || event.ctrlKey || event.shiftKey);
            openProductLink(linkData.url, { newTab: shouldOpenNewTab });
        }
    }

    function parseJSONAttribute(element, attribute) {
        const raw = element.getAttribute(attribute);
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch (error) {
            try {
                return JSON.parse(raw.replace(/&quot;/g, '"'));
            } catch (secondaryError) {
                console.warn('Failed to parse JSON attribute', attribute, secondaryError);
                return null;
            }
        }
    }

    function getSizedImageUrl(src, size) {
        if (!src) return '';
        if (window.Shopify && Shopify.Image && typeof Shopify.Image.getSizedImageUrl === 'function') {
            return Shopify.Image.getSizedImageUrl(src, size);
        }
        const [base, query] = src.split('?');
        const suffix = query ? `?${query}` : '';
        if (/_\d+x\d+\./.test(base)) {
            return base.replace(/_\d+x\d+\./, `_${size}.`) + suffix;
        }
        if (/_x\d+\./.test(base)) {
            return base.replace(/_x\d+\./, `_${size}.`) + suffix;
        }
        return base.replace(/(\.[a-zA-Z]{2,5})$/, `_${size}$1`) + suffix;
    }

    function buildSrcset(src) {
        const widths = [360, 540, 720];
        const entries = widths
            .map((width) => {
                const url = getSizedImageUrl(src, `${width}x`);
                return url ? `${url} ${width}w` : null;
            })
            .filter(Boolean);
        return {
            srcset: entries.join(', '),
            defaultSrc: getSizedImageUrl(src, '720x') || src
        };
    }

    function normalizeColor(value) {
        if (!value) return '';
        const stringValue = value.toString().trim();
        if (/^#?[0-9a-fA-F]{3,8}$/.test(stringValue)) {
            return stringValue.startsWith('#') ? stringValue : `#${stringValue}`;
        }
        return stringValue;
    }

    function lightenColor(hex, ratio) {
        if (!hex || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return '';
        const normalized = hex.replace('#', '');
        const factor = Math.max(0, Math.min(1, ratio || 0.25));
        const expand = normalized.length === 3
            ? normalized.split('').map((char) => char + char).join('')
            : normalized;
        const channels = [
            parseInt(expand.substring(0, 2), 16),
            parseInt(expand.substring(2, 4), 16),
            parseInt(expand.substring(4, 6), 16)
        ];
        const lightened = channels
            .map((channel) => {
                const value = Math.round(channel + (255 - channel) * factor);
                return value.toString(16).padStart(2, '0');
            })
            .join('');
        return `#${lightened}`;
    }

    class ProductCard {
        constructor(element) {
            if (element.dataset.cardInitialized === 'true') {
                return;
            }

            this.card = element;
            this.card.dataset.cardInitialized = 'true';

            this.variantInput = this.card.querySelector(SELECTORS.variantInput);
            this.submitButton = this.card.querySelector(SELECTORS.submitButton);
            this.mediaLink = this.card.querySelector(SELECTORS.mediaLink);
            this.sizeButtons = Array.from(this.card.querySelectorAll(SELECTORS.sizeButton));
            this.productData = parseJSONAttribute(this.card, 'data-json-product') || {};
            this.variantColors = parseJSONAttribute(this.card, 'data-variant-colors') || {};
            this.initialVariantId = Number(this.card.getAttribute('data-initial-variant-id')) || null;
            const linkData = ensureCardLink(this.card);
            this.productUrl = (linkData && linkData.url) || '';
            this.productTitle = (linkData && linkData.title) || '';


            const mainImage = this.mediaLink ? this.mediaLink.querySelector('img') : null;
            this.mainImage = mainImage;
            this.defaultImage = mainImage
                ? {
                      src: mainImage.getAttribute('src'),
                      srcset: mainImage.getAttribute('srcset'),
                      dataSrcset: mainImage.getAttribute('data-srcset'),
                      sizes: mainImage.getAttribute('sizes'),
                      dataSizes: mainImage.getAttribute('data-sizes')
                  }
                : null;

            const computedStyles = window.getComputedStyle(this.card);
            const accentCandidate = (
                this.card.style.getPropertyValue('--card-media-accent') ||
                computedStyles.getPropertyValue('--card-media-accent') ||
                computedStyles.getPropertyValue('--card-media-accent-default') ||
                '#f3e9dd'
            ).trim();
            const baseCandidate = (
                this.card.style.getPropertyValue('--card-media-base') ||
                computedStyles.getPropertyValue('--card-media-base') ||
                computedStyles.getPropertyValue('--card-media-base-default') ||
                '#ffffff'
            ).trim();
            this.defaultAccentColor = accentCandidate || '#f3e9dd';
            this.defaultBaseColor = baseCandidate || '#ffffff';

            this.labels = { ...LABELS };

            this.handleSizeClick = this.handleSizeClick.bind(this);
            this.handleCardClick = this.handleCardClick.bind(this);
            this.handleCardKeydown = this.handleCardKeydown.bind(this);

            this.bindEvents();
            this.setInitialVariant();
        }

        bindEvents() {
            if (this.sizeButtons.length) {
                this.sizeButtons.forEach((button) => {
                    button.addEventListener('click', this.handleSizeClick);
                    button.addEventListener('keydown', (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            button.click();
                        }
                    });
                });
            }

            if (this.productUrl) {
                ensureCardLink(this.card);
            }
        }

        setInitialVariant() {
            let targetId = this.initialVariantId;
            if (targetId && !this.getVariantById(targetId)) {
                targetId = null;
            }

            if (!targetId && this.sizeButtons.length) {
                const activeButton = this.sizeButtons.find((button) =>
                    button.classList.contains('is-selected') && !button.disabled
                );
                const fallbackButton = activeButton || this.sizeButtons.find((button) => !button.disabled);
                if (fallbackButton) {
                    targetId = Number(fallbackButton.dataset.variantId);
                }
            }

            if (!targetId && Array.isArray(this.productData.variants) && this.productData.variants.length) {
                targetId = this.productData.variants[0].id;
            }

            if (targetId) {
                this.selectVariant(targetId, { updateButtons: true });
            }
        }

        handleCardClick(event) {
            if (!this.productUrl || event.defaultPrevented) {
                return;
            }

            if (typeof event.button === 'number' && event.button !== 0) {
                return;
            }

            const selection = window.getSelection ? window.getSelection().toString() : '';
            if (selection) {
                return;
            }

            const interactiveTarget = event.target.closest(INTERACTIVE_ELEMENTS_SELECTOR);
            if (interactiveTarget) {
                return;
            }

            if (event.metaKey || event.ctrlKey) {
                window.open(this.productUrl, '_blank', 'noopener');
                return;
            }

            window.location.assign(this.productUrl);
        }

        handleCardKeydown(event) {
            if (!this.productUrl || event.defaultPrevented) {
                return;
            }

            if (event.target !== this.card) {
                return;
            }

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                window.location.assign(this.productUrl);
            }
        }

        handleSizeClick(event) {
            const button = event.currentTarget;
            if (button.disabled) return;
            const variantId = Number(button.dataset.variantId);
            if (variantId) {
                this.selectVariant(variantId, { updateButtons: true });
            }
        }

        getVariantById(id) {
            if (!this.productData || !Array.isArray(this.productData.variants)) return null;
            return this.productData.variants.find((variant) => Number(variant.id) === Number(id)) || null;
        }

        selectVariant(variantId, { updateButtons = false } = {}) {
            const variant = this.getVariantById(variantId);
            if (!variant) return;

            if (this.variantInput) {
                this.variantInput.value = variant.id;
            }

            if (updateButtons) {
                this.updateSizeButtons(variant.id);
            }

            this.updateSubmitButton(variant);
            this.updateColors(variant);
            this.updateMedia(variant);
        }

        updateSizeButtons(activeId) {
            this.sizeButtons.forEach((button) => {
                const isActive = Number(button.dataset.variantId) === Number(activeId);
                button.classList.toggle('is-selected', isActive);
                button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        }

        updateSubmitButton(variant) {
            if (!this.submitButton) return;
            const isAvailable = Boolean(variant.available);
            this.submitButton.disabled = !isAvailable;
            this.submitButton.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
            this.submitButton.textContent = isAvailable ? this.labels.addToCart : this.labels.soldOut;
        }

        updateColors(variant) {
            const colorValue = this.variantColors ? this.variantColors[variant.id] : null;
            const normalized = normalizeColor(colorValue);
            if (normalized && /^#/.test(normalized)) {
                const lighter = lightenColor(normalized, 0.28) || this.defaultBaseColor;
                this.card.style.setProperty('--card-media-accent', normalized);
                this.card.style.setProperty('--card-media-base', lighter);
                return;
            }
            if (normalized) {
                this.card.style.setProperty('--card-media-accent', normalized);
                this.card.style.setProperty('--card-media-base', this.defaultBaseColor);
                return;
            }
            this.card.style.setProperty('--card-media-accent', this.defaultAccentColor);
            this.card.style.setProperty('--card-media-base', this.defaultBaseColor);
        }

        updateMedia(variant) {
            if (!this.mainImage) return;
            const media = variant.featured_media || null;
            if (!media || !media.preview_image || !media.preview_image.src) {
                this.resetMedia();
                return;
            }

            const preview = media.preview_image;
            const srcsetData = buildSrcset(preview.src);
            const defaultSrc = srcsetData.defaultSrc || preview.src;

            this.mainImage.setAttribute('src', defaultSrc);
            if (srcsetData.srcset) {
                this.mainImage.setAttribute('srcset', srcsetData.srcset);
                this.mainImage.setAttribute('data-srcset', srcsetData.srcset);
            }
            const defaultSizes = this.defaultImage
                ? this.defaultImage.sizes || this.defaultImage.dataSizes
                : null;
            if (defaultSizes) {
                this.mainImage.setAttribute('sizes', defaultSizes);
            }
            if (this.defaultImage && this.defaultImage.dataSizes) {
                this.mainImage.setAttribute('data-sizes', this.defaultImage.dataSizes);
            }

            if (window.lazySizes && this.mainImage.classList.contains('lazyload')) {
                lazySizes.loader.unveil(this.mainImage);
            }
        }

        resetMedia() {
            if (!this.mainImage || !this.defaultImage) return;
            if (this.defaultImage.src) {
                this.mainImage.setAttribute('src', this.defaultImage.src);
            }
            const originalSrcset = this.defaultImage.srcset || this.defaultImage.dataSrcset;
            if (originalSrcset) {
                this.mainImage.setAttribute('srcset', originalSrcset);
            }
            if (this.defaultImage.dataSrcset) {
                this.mainImage.setAttribute('data-srcset', this.defaultImage.dataSrcset);
            }
            if (this.defaultImage.sizes) {
                this.mainImage.setAttribute('sizes', this.defaultImage.sizes);
            }
            if (this.defaultImage.dataSizes) {
                this.mainImage.setAttribute('data-sizes', this.defaultImage.dataSizes);
            }
        }
    }

    function init(root = document) {
        const cards = Array.from(root.querySelectorAll(SELECTORS.card));
        cards.forEach((card) => new ProductCard(card));
    }

    function applyLinkEnhancements(root = document) {
        const cards = Array.from(root.querySelectorAll(CARD_SELECTOR));
        cards.forEach((card) => ensureCardLink(card));
    }

    function observeCardMutations() {
        if (typeof MutationObserver !== 'function') {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            const discovered = new Set();
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof Element)) {
                        return;
                    }
                    if (node.matches && node.matches(CARD_SELECTOR)) {
                        discovered.add(node);
                    }
                    const descendants = node.querySelectorAll ? node.querySelectorAll(CARD_SELECTOR) : [];
                    descendants.forEach((element) => discovered.add(element));
                });
            });

            if (!discovered.size) {
                return;
            }

            discovered.forEach((card) => {
                ensureCardLink(card);
                new ProductCard(card);
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
    }

    function start() {
        init();
        applyLinkEnhancements();
        document.addEventListener('click', handleDelegatedClick);
        document.addEventListener('auxclick', handleDelegatedAuxClick);
        document.addEventListener('keydown', handleDelegatedKeydown);
        observeCardMutations();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }

    document.addEventListener('shopify:section:load', (event) => {
        if (event && event.target) {
            init(event.target);
            applyLinkEnhancements(event.target);
        }
    });
})();
